import React, { MouseEvent as ReactMouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";

export interface NGDropdownListItem<TId> {
  /** Translation key or display string for the item label. */
  name: string;
  /** Value passed to `onUpdate` when this item is selected. */
  id: TId;
  /** Icon class name to render alongside the label. */
  icon?: string;
  /** CSS class for a flag icon (e.g. `"fi fi-se"`). */
  flag?: string;
  /** Image URL to display for the item. */
  img?: string;
  /** Raw React node used as the display label instead of `name` when `rawNames` is enabled. */
  raw?: React.ReactNode;
  /** `data-testid` applied to this specific list item. */
  listItemDataTestId?: string;
  /** When true the item is visible in the list but greyed out and cannot be selected. */
  disabled?: boolean;
}

export interface SXDropdownProps<TId> {
  /** Inline styles applied to the toggle button or input element. */
  style?: any;
  /** `id` for the outermost `<Dropdown>` element. */
  id?: string;
  /** `id` applied specifically to the toggle button or input (overrides `id` for the toggle). */
  toggleId?: string;
  /** Additional class name(s) on the outermost `<Dropdown>` wrapper. */
  className?: string;
  /** Called when the mouse enters a list item. */
  onMouseOverItem?: (e: ReactMouseEvent, v?: any) => void;
  /** Called when the mouse leaves a list item. */
  onMouseOutItem?: (e: ReactMouseEvent) => void;
  /** Called when the user right-clicks the toggle. Receives the event and the current value. */
  onContextMenu?: (e: any, v?: any) => void;
  /** Flat list of selectable items. Ignored when `sections` is provided. */
  list: Array<NGDropdownListItem<TId>>;
  /** Grouped list of items with optional headers and dividers. Takes priority over `list`. */
  sections?: Array<SXDropdownSection<TId>>;
  /** `data-testid` placed on the toggle and the menu for automated testing. */
  dataTestId?: string;
  /** Prefix for `data-testid` on individual list items. */
  listItemDataTestId?: string;
  /** `name` attribute on the toggle button / input element. */
  name: string;
  /** Called when the open/closed state of the dropdown changes. */
  onToggle?: (e: any) => void;
  /** Which direction the menu opens relative to the toggle. Defaults to `"down"`. */
  dropDirection?: "up" | "up-centered" | "start" | "end" | "down" | "down-centered";
  /** Currently selected item. Pass `null` or `undefined` to show the placeholder. */
  val: NGDropdownListItem<TId> | null | undefined;
  /** BCP-47 language code used for localisation (passed through; not used internally). */
  lang: string;
  /** Called with the selected item when the user makes a selection. */
  onUpdate: (value: NGDropdownListItem<TId> | null) => void;
  /** When true the entire dropdown is disabled — the toggle is greyed out and the menu cannot be opened. */
  disabled?: boolean;
  /** Translation key shown in the toggle when no item is selected. Defaults to `"select_.label"`. */
  notSelectedName?: string;
  /** When true the toggle stretches to fill its container width (used inside table cells). */
  tableView?: boolean;
  /** Localisation object passed down to child components. Set to `false` to skip translation. */
  localisation?: any;
  /** When true item labels are rendered as-is without running them through i18n. */
  rawNames?: boolean;
  /** Additional class name applied to every `<Dropdown.Item>`. */
  itemClassName?: string;
  /** Returns an optional React node rendered to the right of the item label. */
  renderItemSuffix?: (item: NGDropdownListItem<TId>) => ReactNode;
  /** When true the toggle is rendered as a text input that filters the list as the user types. */
  editableInput?: boolean;
  /** Controlled value of the editable input. Defaults to the selected item's name. */
  inputValue?: string;
  /** Called whenever the editable input text changes. */
  onInputValueChange?: (value: string) => void;
  /** When false, typing in the editable input will not filter the list. Defaults to true. */
  filterItemsByInput?: boolean;
}

export interface SXDropdownSection<TId> {
  /** Optional heading rendered above the section's items. */
  header?: string;
  /** When true a `<Dropdown.Divider>` is rendered before this section. */
  showDividerBefore?: boolean;
  /** Text shown when `items` is empty. */
  emptyText?: string;
  /** The items belonging to this section. */
  items: Array<NGDropdownListItem<TId>>;
  /** Prefix for item element `id` attributes within this section. */
  itemIdPrefix?: string;
}

const DropdownToggle = <TId,>({
  id,
  dataTestId,
  name,
  val,
  notSelectedName,
  flag,
  tableView,
  disabled,
  style,
  localisation,
  rawNames,
}: {
  id?: string;
  dataTestId?: string;
  name: string;
  val?: NGDropdownListItem<TId> | null;
  notSelectedName: string;
  flag: string;
  tableView?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  localisation?: any;
  rawNames?: boolean;
}) => {
  const { t } = useTranslation();
  const localize = localisation !== false && !rawNames;
  const selectedText = !val?.name
    ? t(notSelectedName)
    : localize
      ? t(val.name.endsWith(".label") ? val.name : `${val.name}.label`)
      : (val.raw ?? val.name);
  const selectedTitle = typeof selectedText === "string" ? selectedText : undefined;

  return (
    <Dropdown.Toggle
      id={id ?? ""}
      variant="default"
      className={`dropdown-basic${disabled ? " disabled" : ""} ${tableView ? "table-view" : "dropdown-border-style"} `}
      data-testid={dataTestId}
      name={name}
      style={{ width: tableView ? "100%" : undefined, paddingLeft: tableView ? 5 : 8, ...style }}
      title={selectedTitle}
    >
      <span style={{ marginRight: 2 }} className={val?.flag ?? flag}></span>
      <span
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          maxWidth: "100%",
          lineHeight: "1.2",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: 16,
        }}
      >
        {selectedText}
      </span>
    </Dropdown.Toggle>
  );
};

export const SXDropdown = <TId,>(props: SXDropdownProps<TId>) => {
  const NO_SELECTED_NAME = props.notSelectedName ?? "select_.label";
  const NO_SELECTED_FLAG = "";
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number>();
  const [isEditableOpen, setIsEditableOpen] = useState(false);

  const isEditable = !!props.editableInput;
  const inputValue = props.inputValue ?? props.val?.name ?? "";
  const shouldLockDropDirection = props.dropDirection !== undefined;

  const syncMenuWidth = () => {
    if (props.tableView) {
      setMenuWidth(undefined);
      return;
    }

    const toggle =
      dropdownRef.current?.querySelector<HTMLElement>(".dropdown-toggle") ??
      dropdownRef.current?.querySelector<HTMLElement>(".sx-dropdown-input-toggle");
    if (!toggle) {
      return;
    }

    setMenuWidth(toggle.offsetWidth);
  };

  useLayoutEffect(() => {
    syncMenuWidth();

    if (props.tableView) {
      return;
    }

    const toggle =
      dropdownRef.current?.querySelector<HTMLElement>(".dropdown-toggle") ??
      dropdownRef.current?.querySelector<HTMLElement>(".sx-dropdown-input-toggle");
    if (!toggle || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => syncMenuWidth());
    observer.observe(toggle);
    window.addEventListener("resize", syncMenuWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncMenuWidth);
    };
  }, [props.tableView, props.val?.name, props.inputValue]);

  useEffect(() => {
    if (!isEditable || !isEditableOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsEditableOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditable, isEditableOpen]);

  const effectiveList =
    isEditable && props.filterItemsByInput !== false && inputValue.trim() !== ""
      ? props.list.filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()))
      : props.list;

  const renderItems = (items: Array<NGDropdownListItem<TId>>, itemIdPrefix?: string, keyPrefix = "default") => {
    return items.map((v, index) => {
      const nameWithLabel = v.name.endsWith(".label") ? v.name : v.name + ".label";
      const localize = props.localisation !== false && !props.rawNames;
      const displayName = localize ? t(nameWithLabel) : (v.raw ?? v.name);
      const itemSuffix = props.renderItemSuffix?.(v);

      return (
        <Dropdown.Item
          id={itemIdPrefix ? `${itemIdPrefix}_${index}` : `${props.name}_option_${index}`}
          key={`${keyPrefix}_${index}_${String(v.id)}`}
          data-testid={`${props.listItemDataTestId}-dropdown-item-${nameWithLabel}`}
          onClick={() => {
            if (v.disabled) return;
            props.onUpdate(v);
            if (isEditable) {
              setIsEditableOpen(false);
            }
          }}
          onMouseOver={(e) => props.onMouseOverItem && props.onMouseOverItem(e, v)}
          onMouseOut={(e) => props.onMouseOutItem && props.onMouseOutItem(e)}
          style={{ zIndex: 99999, whiteSpace: "nowrap", overflowWrap: "normal" }}
          className={props.itemClassName}
          disabled={v.disabled}
        >
          <span style={{ marginRight: 2 }} className={v.flag}></span>
          {itemSuffix ? (
            <span className="d-flex justify-content-between align-items-center w-100">
              <span>{displayName}</span>
              <span>{itemSuffix}</span>
            </span>
          ) : (
            displayName
          )}
        </Dropdown.Item>
      );
    });
  };

  return (
    <Dropdown
      ref={dropdownRef}
      className={props.className}
      data-testid={props.dataTestId}
      show={isEditable ? isEditableOpen : undefined}
      onContextMenu={(e) => props.onContextMenu && props.onContextMenu(e, props.val)}
      onToggle={(isOpen, metadata) => {
        if (isEditable) {
          const source = metadata?.source;

          if (isOpen) {
            setIsEditableOpen(true);
          } else if (source === "rootClose") {
            const eventTarget = metadata?.originalEvent?.target as Node | null | undefined;
            const isInsideDropdown = !!eventTarget && !!dropdownRef.current?.contains(eventTarget);

            if (!isInsideDropdown) {
              setIsEditableOpen(false);
            }
          } else if (source === "select" || source === "keydown") {
            setIsEditableOpen(false);
          }
        }
        if (isOpen && !props.tableView) {
          syncMenuWidth();
        }
        props.onToggle && props.onToggle(isOpen);
      }}
      id={props.id ?? ""}
      drop={props.dropDirection ?? "down"}
    >
      {isEditable ? (
        <div style={{ position: "relative", width: props.tableView ? "100%" : undefined }}>
          <Form.Control
            id={props.toggleId ?? props.id}
            data-testid={props.dataTestId}
            name={props.name}
            type="text"
            value={inputValue}
            disabled={props.disabled}
            className={`sx-dropdown-input-toggle dropdown-basic ${props.tableView ? "table-view" : "dropdown-border-style"} ${
              props.disabled ? "disabled" : ""
            }`}
            style={{
              width: props.tableView ? "100%" : undefined,
              paddingLeft: props.tableView ? 5 : 8,
              ...props.style,
            }}
            onFocus={() => {
              if (!props.disabled) {
                setIsEditableOpen(true);
                syncMenuWidth();
              }
            }}
            onClick={() => {
              if (!props.disabled) {
                setIsEditableOpen(true);
                syncMenuWidth();
              }
            }}
            onChange={(e) => {
              props.onInputValueChange?.(e.target.value);
              if (!props.disabled) {
                setIsEditableOpen(true);
              }
            }}
            placeholder={t(NO_SELECTED_NAME)}
            autoComplete="off"
          />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: isEditableOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
              transformOrigin: "center",
              transition: "transform 120ms ease",
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "5px solid var(--dropdown-caret, #666)",
              pointerEvents: "none",
              opacity: props.disabled ? 0.5 : 1,
            }}
          />
        </div>
      ) : (
        <DropdownToggle
          id={props.toggleId ?? props.id}
          dataTestId={props.dataTestId}
          name={props.name}
          val={props.val}
          notSelectedName={NO_SELECTED_NAME}
          flag={NO_SELECTED_FLAG}
          tableView={props.tableView}
          disabled={props.disabled}
          style={props.style}
          localisation={props.localisation}
          rawNames={props.rawNames}
        />
      )}
      {!props.disabled && (
        <Dropdown.Menu
          style={{
            zIndex: 99999,
            width: "max-content",
            maxWidth: "none",
            whiteSpace: "nowrap",
            ...(!props.tableView && menuWidth ? { minWidth: `${menuWidth}px` } : {}),
          }}
          renderOnMount
          popperConfig={{
            strategy: "absolute",
            ...(shouldLockDropDirection
              ? {
                  modifiers: [
                    { name: "flip", enabled: false },
                    { name: "preventOverflow", enabled: false },
                  ],
                }
              : {}),
          }}
          data-testid={props.dataTestId}
        >
          {props.sections && props.sections.length > 0
            ? props.sections.map((section, sectionIndex) => {
                return (
                  <React.Fragment key={`section_${sectionIndex}_${section.header ?? "no-header"}`}>
                    {section.showDividerBefore && <Dropdown.Divider />}
                    {section.header && <Dropdown.Header>{section.header}</Dropdown.Header>}
                    {section.items.length === 0 && section.emptyText ? (
                      <Dropdown.ItemText>{section.emptyText}</Dropdown.ItemText>
                    ) : (
                      renderItems(section.items, section.itemIdPrefix, `section_${sectionIndex}`)
                    )}
                  </React.Fragment>
                );
              })
            : renderItems(effectiveList)}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};
